import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Circle, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const ROLE_LABELS = { user: 'Kullanıcı', admin: 'Admin', superadmin: 'Süper Admin' };
const PACKAGE_LABELS = { free: 'Free', pro: 'Pro', platinum: 'Platinum' };

const formatResetTime = (value) => {
  if (!value) return '—';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SuperAdminUsersPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordUpdates, setPasswordUpdates] = useState({});
  const [roleUpdates, setRoleUpdates] = useState({});
  const [packageUpdates, setPackageUpdates] = useState({});
  const [overrideUpdates, setOverrideUpdates] = useState({});
  const [periodUpdates, setPeriodUpdates] = useState({});
  const [users, setUsers] = useState([]);
  const [showOnlyActive, setShowOnlyActive]       = useState(false);
  const [showOnlyPlatinum, setShowOnlyPlatinum]   = useState(false);
  const [searchQuery, setSearchQuery]             = useState('');
  const [createForm, setCreateForm] = useState({
    email: '', full_name: '', password: '', role: 'user', approved: true, allow_multi_login: false,
  });
  const [pendingUsers, setPendingUsers]   = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approvingId, setApprovingId]     = useState(null);

  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      if (a.role === 'superadmin') return -1;
      if (b.role === 'superadmin') return 1;
      const aName = (a.full_name || a.email || '').trim();
      const bName = (b.full_name || b.email || '').trim();
      return aName.localeCompare(bName, 'tr');
    });
  }, [users]);

  const visibleUsers = useMemo(() => {
    let list = sortedUsers;
    if (showOnlyActive)   list = list.filter((u) => Boolean(u.is_active));
    if (showOnlyPlatinum) list = list.filter((u) => u.package === 'platinum');
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(
        (u) =>
          (u.email || '').toLowerCase().includes(q) ||
          (u.full_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [showOnlyActive, showOnlyPlatinum, searchQuery, sortedUsers]);

  const activeUsersCount = useMemo(() => users.filter((u) => Boolean(u.is_active)).length, [users]);
  const platinumUsersCount = useMemo(() => users.filter((u) => u.package === 'platinum').length, [users]);

  const fetchUsers = async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users`, { headers: getAuthHeader() });
      setUsers(response.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Kullanıcılar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/pending-users`, { headers: getAuthHeader() });
      setPendingUsers(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Onay bekleyen kullanıcılar yüklenemedi');
    } finally {
      setPendingLoading(false);
    }
  };

  const approveUser = async (userId) => {
    setApprovingId(userId);
    try {
      await axios.post(`${API_URL}/admin/pending-users/${userId}/approve`, {}, { headers: getAuthHeader() });
      toast.success('Kullanıcı onaylandı');
      await fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Onay işlemi başarısız');
    } finally {
      setApprovingId(null);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.full_name) {
      toast.error('Lütfen tüm alanları doldurun.');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/create`, createForm, { headers: getAuthHeader() });
      toast.success('Yeni kullanıcı oluşturuldu.');
      setCreateForm({ email: '', full_name: '', password: '', role: 'user', approved: true, allow_multi_login: false });
      await fetchUsers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Kullanıcı oluşturulamadı.');
    } finally {
      setSaving(false);
    }
  };

  const updatePassword = async (targetUser) => {
    const nextPassword = (passwordUpdates[targetUser.id] || '').trim();
    if (!nextPassword) { toast.error('Yeni şifre boş olamaz.'); return; }
    if (nextPassword.length < 6) { toast.error('Yeni şifre en az 6 karakter olmalı.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/${targetUser.id}/password`, { new_password: nextPassword }, { headers: getAuthHeader() });
      toast.success(`${targetUser.email} şifresi güncellendi.`);
      setPasswordUpdates((prev) => ({ ...prev, [targetUser.id]: '' }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Şifre güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const toggleMultiLogin = async (targetUser, allowValue) => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/users/${targetUser.id}/toggle-multi-login`, { allow_multi_login: allowValue }, { headers: getAuthHeader() });
      toast.success(`${targetUser.email} için çoklu giriş ayarı güncellendi.`);
      setUsers((prev) => prev.map((u) => u.id === targetUser.id ? { ...u, allow_multi_login: allowValue } : u));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Çoklu giriş ayarı güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (targetUser) => {
    const newRole = roleUpdates[targetUser.id];
    if (!newRole || newRole === targetUser.role) { toast.info('Rol değişmedi.'); return; }
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/admin/users/${targetUser.id}/role`, { role: newRole }, { headers: getAuthHeader() });
      toast.success(`${targetUser.email} rolü "${ROLE_LABELS[newRole]}" olarak güncellendi.`);
      await fetchUsers();
      setRoleUpdates((prev) => { const n = { ...prev }; delete n[targetUser.id]; return n; });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Rol güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const updatePackage = async (targetUser) => {
    const newPackage = packageUpdates[targetUser.id] || targetUser.package || 'free';
    const overrideRaw = overrideUpdates[targetUser.id];
    const daily_limit_override = overrideRaw !== undefined && overrideRaw !== ''
      ? parseInt(overrideRaw, 10)
      : null;
    const subscription_period = periodUpdates[targetUser.id] ?? targetUser.subscription_period ?? 'monthly';
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/admin/users/${targetUser.id}/package`,
        { package: newPackage, daily_limit_override, subscription_period: newPackage === 'free' ? null : subscription_period },
        { headers: getAuthHeader() }
      );
      toast.success(`${targetUser.email} paketi güncellendi.`);
      await fetchUsers();
      setPackageUpdates((prev) => { const n = { ...prev }; delete n[targetUser.id]; return n; });
      setOverrideUpdates((prev) => { const n = { ...prev }; delete n[targetUser.id]; return n; });
      setPeriodUpdates((prev) => { const n = { ...prev }; delete n[targetUser.id]; return n; });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Paket güncellenemedi.');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (targetUser) => {
    if (!targetUser?.id) return;
    if (!window.confirm(`${targetUser.email} hesabını silmek istediğinize emin misiniz?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/admin/users/${targetUser.id}`, { headers: getAuthHeader() });
      toast.success(`${targetUser.email} hesabı silindi.`);
      setUsers((prev) => prev.filter((u) => u.id !== targetUser.id));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Kullanıcı silinemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#F6F1EA]">
        <Sidebar />
        <main className="lg:ml-64 pt-16 lg:pt-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Card className="border-[#E6DCCF]">
              <CardHeader>
                <CardTitle className="text-[#2E2620]">Yetkisiz Erişim</CardTitle>
                <CardDescription className="text-[#7A6A5C]">Bu sayfayı yalnızca admin ve süper admin görüntüleyebilir.</CardDescription>
              </CardHeader>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="super-admin-users-page">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-[#2E2620] font-['Playfair_Display']">Yönetim Paneli</h1>
            <p className="text-[#7A6A5C] mt-2">Kullanıcıları, rolleri, paketleri ve günlük limitleri yönetin.</p>
          </div>

          <Card className="border-[#E6DCCF]">
            <CardContent className="p-4 space-y-3">
              {/* Arama kutusu */}
              <Input
                placeholder="İsim veya e-posta ile ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-[#E6DCCF] focus-visible:ring-[#C86F4A]"
              />
              {/* Sayaçlar + Filtreler */}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge variant="secondary" className="bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]">
                  <Circle className="w-3 h-3 fill-current mr-1" />
                  Aktif: {activeUsersCount}
                </Badge>
                <Badge variant="secondary" className="bg-[#8f969e]/20 text-[#3c3e42] border border-[#8f969e]/30">
                  Platinum: {platinumUsersCount}
                </Badge>
                <Badge variant="secondary" className="bg-[#F3F4F6] text-[#374151] border border-[#D1D5DB]">
                  Toplam: {sortedUsers.length}
                </Badge>
                <div className="ml-auto flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch checked={showOnlyActive} onCheckedChange={setShowOnlyActive} />
                    <span className="text-[#7A6A5C]">Sadece aktifler</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={showOnlyPlatinum} onCheckedChange={setShowOnlyPlatinum} />
                    <span className="text-[#7A6A5C]">Sadece platinumlar</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="users" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#E8D9C7]">
              <TabsTrigger value="users">Kullanıcılar</TabsTrigger>
              <TabsTrigger value="pending">
                Onay Bekleyenler
                {pendingUsers.length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#C86F4A] text-white text-[10px] font-bold">
                    {pendingUsers.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ─── USERS TAB ─── */}
            <TabsContent value="users" className="space-y-6 mt-4">

              {/* Create User */}
              {/* {isSuperAdmin && (
                <Card className="border-[#E6DCCF]">
                  <CardHeader>
                    <CardTitle className="text-[#2E2620]">Yeni Kullanıcı Oluştur</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>E-posta</Label>
                        <Input type="email" value={createForm.email} onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))} placeholder="kullanici@email.com" />
                      </div>
                      <div className="space-y-2">
                        <Label>Ad Soyad</Label>
                        <Input value={createForm.full_name} onChange={(e) => setCreateForm((p) => ({ ...p, full_name: e.target.value }))} placeholder="Ad Soyad" />
                      </div>
                      <div className="space-y-2">
                        <Label>Şifre</Label>
                        <Input type="text" value={createForm.password} onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))} placeholder="En az 6 karakter" />
                      </div>
                      <div className="space-y-2">
                        <Label>Rol</Label>
                        <select value={createForm.role} onChange={(e) => setCreateForm((p) => ({ ...p, role: e.target.value }))} className="w-full h-10 px-3 rounded-md border border-[#E6DCCF] bg-white text-[#2E2620]">
                          <option value="user">Kullanıcı</option>
                          <option value="admin">Admin</option>
                          <option value="superadmin">Süper Admin</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch checked={createForm.approved} onCheckedChange={(v) => setCreateForm((p) => ({ ...p, approved: v }))} />
                        <Label>Hesap onaylı oluşturulsun</Label>
                      </div>
                      <div className="flex items-center gap-3">
                        <Switch checked={createForm.allow_multi_login} onCheckedChange={(v) => setCreateForm((p) => ({ ...p, allow_multi_login: v }))} />
                        <Label>Çoklu girişe izin ver</Label>
                      </div>
                      <div className="md:col-span-2">
                        <Button type="submit" disabled={saving} className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full">
                          {saving ? 'Kaydediliyor...' : 'Kullanıcı Oluştur'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )} */}

              {/* User List */}
              <Card className="border-[#E6DCCF]">
                <CardHeader>
                  <CardTitle className="text-[#2E2620]">Mevcut Kullanıcılar</CardTitle>
                  <CardDescription className="text-[#7A6A5C]">
                    {loading ? 'Yükleniyor...' : `${visibleUsers.length} kullanıcı${showOnlyActive ? ' (aktif filtreli)' : ''}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-[#7A6A5C]">Yükleniyor...</div>
                  ) : (
                    <div className="space-y-4">
                      {visibleUsers.map((item) => (
                        <div key={item.id} className="border border-[#E6DCCF] rounded-lg p-4 bg-white space-y-3">
                          {/* Header row */}
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-medium text-[#2E2620]">{item.full_name || item.email}</div>
                            <Badge variant="secondary" className="bg-[#E8D9C7] text-[#2E2620]">{ROLE_LABELS[item.role] || item.role}</Badge>
                            <Badge variant="secondary" className={
                              item.package === 'platinum' ? 'bg-[#8f969e] text-[#3c3e42]' :
                              item.package === 'pro' ? 'bg-[#EDE9FE] text-[#5B21B6]' :
                              'bg-[#F3F4F6] text-[#374151]'
                            }>{PACKAGE_LABELS[item.package] || item.package || 'Free'}</Badge>
                            <Badge variant="secondary" className={item.is_active ? 'bg-[#DCFCE7] text-[#166534] border border-[#86EFAC]' : 'bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]'}>
                              <Circle className="w-3 h-3 fill-current mr-1" />
                              {item.is_active ? 'Aktif' : 'Pasif'}
                            </Badge>
                            {!item.approved && <Badge variant="secondary" className="bg-[#FEE2E2] text-[#991B1B]">Onaysız</Badge>}
                          </div>

                          {/* Email + quota */}
                          <div className="text-sm text-[#7A6A5C]">{item.email}</div>
                          <div className="text-sm text-[#7A6A5C] flex flex-wrap gap-x-4 gap-y-1">
                            <span>Günlük Sorgu: <strong className="text-[#2E2620]">{item.daily_queries_used ?? 0}</strong></span>
                            <span>Limit: <strong className="text-[#2E2620]">{item.daily_queries_limit ?? 'Sınırsız'}</strong></span>
                            <span>Kalan: <strong className="text-[#2E2620]">{item.daily_queries_remaining ?? 'Sınırsız'}</strong></span>
                            {item.daily_limit_override != null && (
                              <span className="text-[#C86F4A]">Bireysel override: <strong>{item.daily_limit_override === -1 ? 'Sınırsız' : item.daily_limit_override}</strong></span>
                            )}
                            {item.daily_limit_resets_at && (
                              <span>Sıfırlanma: <strong className="text-[#2E2620]">{formatResetTime(item.daily_limit_resets_at)}</strong></span>
                            )}
                          </div>

                          {/* Password + multi-login */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                            <Input type="text" placeholder="Yeni şifre" value={passwordUpdates[item.id] || ''} onChange={(e) => setPasswordUpdates((p) => ({ ...p, [item.id]: e.target.value }))} />
                            <Button type="button" disabled={saving} onClick={() => updatePassword(item)} className="bg-[#6D7C3B] hover:bg-[#5F6E34] text-white rounded-full">
                              Şifreyi Güncelle
                            </Button>
                            <div className="flex items-center gap-2">
                              <Switch checked={Boolean(item.allow_multi_login)} onCheckedChange={(v) => toggleMultiLogin(item, v)} disabled={saving} />
                              <span className="text-sm text-[#7A6A5C]">Çoklu giriş</span>
                            </div>
                          </div>

                          {/* Role change (superadmin only) */}
                          {isSuperAdmin && item.id !== user?.id && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center pt-2 border-t border-[#E6DCCF]">
                              <div className="space-y-1">
                                <Label className="text-xs text-[#7A6A5C]">Rol Değiştir</Label>
                                <select
                                  value={roleUpdates[item.id] ?? item.role}
                                  onChange={(e) => setRoleUpdates((p) => ({ ...p, [item.id]: e.target.value }))}
                                  className="w-full h-9 px-3 rounded-md border border-[#E6DCCF] bg-white text-[#2E2620] text-sm"
                                >
                                  <option value="user">Kullanıcı</option>
                                  <option value="admin">Admin</option>
                                  <option value="superadmin">Süper Admin</option>
                                </select>
                              </div>
                              <Button type="button" disabled={saving} onClick={() => updateRole(item)} className="bg-[#2E2620] hover:bg-[#1a1410] text-white rounded-full text-sm">
                                Rolü Kaydet
                              </Button>
                            </div>
                          )}

                          {/* Package + override (admin+) */}
                          {item.role === 'user' && (
                            <div className="pt-2 border-t border-[#E6DCCF] space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                                <div className="space-y-1">
                                  <Label className="text-xs text-[#7A6A5C]">Paket</Label>
                                  <select
                                    value={packageUpdates[item.id] ?? item.package ?? 'free'}
                                    onChange={(e) => setPackageUpdates((p) => ({ ...p, [item.id]: e.target.value }))}
                                    className="w-full h-9 px-3 rounded-md border border-[#E6DCCF] bg-white text-[#2E2620] text-sm"
                                  >
                                    <option value="free">Free</option>
                                    <option value="pro">Pro</option>
                                    <option value="platinum">Platinum</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-[#7A6A5C]">Abonelik Periyodu</Label>
                                  <select
                                    value={periodUpdates[item.id] ?? item.subscription_period ?? 'monthly'}
                                    onChange={(e) => setPeriodUpdates((p) => ({ ...p, [item.id]: e.target.value }))}
                                    disabled={(packageUpdates[item.id] ?? item.package) === 'free'}
                                    className="w-full h-9 px-3 rounded-md border border-[#E6DCCF] bg-white text-[#2E2620] text-sm disabled:opacity-40"
                                  >
                                    <option value="monthly">Aylık (30 gün)</option>
                                    <option value="yearly">Yıllık (365 gün)</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-[#7A6A5C]">Bireysel limit override</Label>
                                  <Input
                                    type="number"
                                    placeholder={`Paket: ${item.daily_queries_limit ?? '—'}`}
                                    value={overrideUpdates[item.id] ?? (item.daily_limit_override != null ? String(item.daily_limit_override) : '')}
                                    onChange={(e) => setOverrideUpdates((p) => ({ ...p, [item.id]: e.target.value }))}
                                    className="h-9 text-sm"
                                  />
                                </div>
                                <Button type="button" disabled={saving} onClick={() => updatePackage(item)} className="bg-[#6D7C3B] hover:bg-[#5F6E34] text-white rounded-full text-sm">
                                  Paketi Kaydet
                                </Button>
                              </div>
                              {/* Abonelik tarihleri */}
                              {item.package !== 'free' && item.subscription_start_date && (
                                <div className="flex flex-wrap gap-4 text-xs text-[#7A6A5C] bg-[#F6F1EA] rounded-lg px-3 py-2">
                                  <span>📅 Başlangıç: <strong className="text-[#2E2620]">{new Date(item.subscription_start_date).toLocaleDateString('tr-TR')}</strong></span>
                                  <span>⏳ Bitiş: <strong className="text-[#2E2620]">{new Date(item.subscription_end_date).toLocaleDateString('tr-TR')}</strong></span>
                                  <span>🔄 Periyot: <strong className="text-[#2E2620]">{item.subscription_period === 'yearly' ? 'Yıllık' : 'Aylık'}</strong></span>
                                  {(() => {
                                    const days = Math.ceil((new Date(item.subscription_end_date) - new Date()) / 86400000);
                                    return <span className={days <= 7 ? 'text-[#B04832] font-semibold' : ''}>🗓 Kalan: <strong>{days > 0 ? `${days} gün` : 'Süresi doldu'}</strong></span>;
                                  })()}
                                </div>
                              )}
                              <p className="text-xs text-[#A89F91]">-1 yazarsanız sınırsız olur. Boş bırakırsanız paket limiti geçerli olur.</p>
                            </div>
                          )}

                          {/* Delete */}
                          <div className="pt-2 border-t border-[#E6DCCF] flex justify-end">
                            <Button type="button" variant="outline" disabled={saving || item.id === user?.id || item.role === 'superadmin'} onClick={() => deleteUser(item)} className="border-[#B04832]/40 text-[#B04832] hover:bg-[#B04832]/10 rounded-full">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Kullanıcıyı Sil
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── PENDING APPROVALS TAB ─── */}
            <TabsContent value="pending" className="mt-4">
              <Card className="border-[#E6DCCF]">
                <CardHeader>
                  <CardTitle className="text-[#2E2620]">Onay Bekleyen Kullanıcılar</CardTitle>
                  <CardDescription className="text-[#7A6A5C]">
                    {pendingLoading ? 'Yükleniyor...' : `${pendingUsers.length} kişi onay bekliyor`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingLoading ? (
                    <div className="text-[#7A6A5C] text-sm">Yükleniyor...</div>
                  ) : pendingUsers.length === 0 ? (
                    <div className="text-[#7A6A5C] text-sm">Onay bekleyen kullanıcı yok.</div>
                  ) : (
                    <div className="space-y-3">
                      {pendingUsers.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between gap-4 bg-white border border-[#E6DCCF] rounded-lg p-4"
                        >
                          <div className="min-w-0">
                            <div className="text-[#2E2620] font-medium truncate">{u.full_name}</div>
                            <div className="text-sm text-[#7A6A5C] truncate">{u.email}</div>
                            <div className="text-xs text-[#A89F91] mt-1">Kayıt: {u.created_at}</div>
                          </div>
                          <Button
                            onClick={() => approveUser(u.id)}
                            disabled={approvingId === u.id}
                            className="flex-shrink-0 bg-[#6D7C3B] hover:bg-[#5F6E34] text-white rounded-full"
                          >
                            {approvingId === u.id ? 'Onaylanıyor...' : 'Onayla'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default SuperAdminUsersPage;
